import { forwardRef } from 'react';
import type { SelectProps as MuiSelectProps } from '@mui/material/Select';
import { TextField, TextFieldProps } from '../TextField';

const HiddenIconComponent = () => null;
const EMPTY_ARRAY: string[] = [];

/**
 * Select component props extending the custom TextField with select mode.
 */
export interface SelectProps extends Omit<TextFieldProps, 'select' | 'onChange' | 'value'> {
  /**
   * The current selected value.
   * For single select, use a string.
   * For multiple select, use a string array.
   */
  value?: string | string[];

  /**
   * Callback fired when the selected value changes.
   * Receives the new value directly (not an event object).
   * For single select, receives a string.
   * For multiple select, receives a string array.
   *
   * @param value - The new selected value (string for single, string[] for multiple)
   */
  onChange?: (value: string | string[]) => void;

  /**
   * The option elements to render (MenuItem for dropdown, option for native).
   */
  children: React.ReactNode;

  /**
   * If true, uses native HTML select element.
   * If false, uses MUI dropdown menu.
   *
   * @default true
   */
  native?: boolean;

  /**
   * If true, hides the dropdown icon.
   * Useful for read-only states or custom styling.
   *
   * @default false
   */
  hideIcon?: boolean;

  /**
   * Props applied to the Select element.
   * Allows advanced MUI Select customization when needed.
   * For multiple selection, pass `multiple: true` here.
   */
  SelectProps?: Partial<MuiSelectProps>;
}

/**
 * Select component - A dropdown selection field built on the custom TextField.
 *
 * **Features:**
 * - Simplified onChange API (receives value directly, not event)
 * - Native HTML select or MUI dropdown menu
 * - Optional icon hiding for read-only appearance
 * - Extends all TextField props (error, helperText, disabled, etc.)
 * - Forward ref support
 *
 * **Common Use Cases:**
 * - Form dropdowns with validation
 * - Filterable option lists
 * - Settings and preferences
 * - Read-only display of selected values
 *
 * @example
 * ```tsx
 * // Native HTML select (default)
 * <Select label="Choose Option" value={value} onChange={setValue} native>
 *   <option value="">Select...</option>
 *   <option value="a">Option A</option>
 *   <option value="b">Option B</option>
 * </Select>
 *
 * // MUI dropdown menu
 * <Select label="Choose Option" value={value} onChange={setValue} native={false}>
 *   <MenuItem value="">Select...</MenuItem>
 *   <MenuItem value="a">Option A</MenuItem>
 *   <MenuItem value="b">Option B</MenuItem>
 * </Select>
 *
 * // With validation error
 * <Select
 *   label="Required Field"
 *   value={value}
 *   onChange={setValue}
 *   error={!value}
 *   helperText={!value ? "This field is required" : ""}
 *   required
 * >
 *   {options}
 * </Select>
 *
 * // Read-only appearance (no icon)
 * <Select
 *   label="Status"
 *   value={status}
 *   disabled
 *   hideIcon
 *   native={false}
 * >
 *   {statusOptions}
 * </Select>
 * ```
 */
export const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      value,
      onChange,
      children,
      native = true,
      hideIcon = false,
      SelectProps,
      sx,
      slotProps: callerSlotProps,
      ...props
    },
    ref
  ) => {
    const isMultiple = SelectProps?.multiple === true;

    const handleChange = onChange
      ? (newValue: string | number | string[]) => {
          if (isMultiple && Array.isArray(newValue)) {
            onChange(newValue);
          } else if (!isMultiple && typeof newValue === 'string') {
            onChange(newValue);
          } else if (typeof newValue === 'number') {
            onChange(String(newValue));
          }
        }
      : undefined;

    const selectValue = value ?? (isMultiple ? EMPTY_ARRAY : '');

    return (
      <TextField
        ref={ref}
        select
        value={selectValue as string | number}
        onChange={handleChange}
        autoSelectOnFocus={false}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
          },
          ...sx,
        }}
        slotProps={{
          inputLabel: { shrink: true, ...callerSlotProps?.inputLabel },
          select: {
            ...(SelectProps as object),
            native,
            ...(hideIcon ? { IconComponent: HiddenIconComponent } : {}),
            ...callerSlotProps?.select,
          },
        }}
        {...props}
      >
        {children}
      </TextField>
    );
  }
);

Select.displayName = 'Select';
